/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState, type KeyboardEvent } from 'react';

import { css, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge';
import Modal, { ModalBody, ModalHeader, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import remixIllustrationSvg from './assets/Remix-Illustration.svg';
import { SUB_TYPE_ICON_MAP } from './constants';
import messages from './messages';
import type { RemixOption, RemixOptionKind, RemixOptionSelectionParameters, RemixSubType } from './types';
import { REMIX_OPTIONS } from './utils';

export type { KnownSubType, RemixOptionKind, RemixOptionSelectionParameters } from './types';
export { getSubTypeDisplayName } from './utils';

// ── Shared transition constant ─────────────────────────────────────────────
const REVEAL_TRANSITION = '500ms cubic-bezier(0.4, 0, 0.2, 1)';

// ── RemixOverlayHeader styles ──────────────────────────────────────────────
const headerWrapperStyle = css({
	position: 'relative',
	width: '100%',
});

const labsPositionStyle = css({
	position: 'absolute',
	top: `calc(-1 * ${token('space.100')})`,
	right: `calc(-1 * ${token('space.100')})`,
	cursor: 'default',
});

const headerIconWrapperStyle = css({
	position: 'relative',
	width: '64px',
	height: '64px',
});

const headerIconImageStyle = css({
	position: 'absolute',
	inset: 0,
	width: '100%',
	height: '100%',
	objectFit: 'cover',
});

const subtitleStyle = css({
	color: token('color.text.subtlest'),
});

// ── RemixSubTypeGrid styles ────────────────────────────────────────────────
const gridStyles = cssMap({
	grid: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: token('space.100'),
		width: '100%',
		paddingBlockStart: token('space.100'),
	},
	tile: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '48px',
		height: '48px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.tile'),
		backgroundColor: token('elevation.surface.overlay'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			borderColor: token('color.border.focused'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

// ── RemixAccordionOption styles ────────────────────────────────────────────
const accordionStyles = cssMap({
	accordionWrapper: {
		width: '100%',
	},
	accordionHeader: {
		width: '100%',
		height: '48px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		backgroundColor: token('elevation.surface.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.150'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	iconContainer: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.tile'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.075'),
	},
	pickForMeWrapper: {
		paddingBlockStart: token('space.100'),
	},
});

const revealWrapperBaseStyle = css({
	overflow: 'hidden',
});

const revealExpandedStyle = css({
	// Accommodates up to ~6 sub-type tiles (2 rows) + Pick for me button.
	// Increase if categories gain more sub-types in the future.
	maxHeight: '300px',
	opacity: 1,
	paddingBlockStart: token('space.050'),
	paddingBlockEnd: token('space.100'),
	// Height and padding expand first, then content fades in
	transition: `max-height ${REVEAL_TRANSITION}, padding ${REVEAL_TRANSITION}, opacity 300ms cubic-bezier(0.4, 0, 0.2, 1) 150ms`,
});

const revealCollapsedStyle = css({
	maxHeight: '0px',
	opacity: 0,
	paddingBlockStart: '0px',
	paddingBlockEnd: '0px',
	pointerEvents: 'none',
	// Content fades out first, then height collapses
	transition: `opacity 200ms ease-out, max-height ${REVEAL_TRANSITION} 100ms, padding ${REVEAL_TRANSITION} 100ms`,
});

const pickForMeButtonWeightStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'button': {
		fontWeight: token('font.weight.regular'),
	},
});

// ── RemixOverlay styles ────────────────────────────────────────────────────
const modalContentWrapperStyle = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
	backgroundColor: token('elevation.surface.sunken'),
	borderRadius: 'inherit',
});

const scrollableBodyStyle = css({
	overflowY: 'auto',
	flex: '1 1 auto',
});

const remixStyles = cssMap({
	describeInputBorderWrapper: {
		width: '100%',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	describeInputWrapper: {
		width: '100%',
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	describePromptRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-end',
		gap: token('space.075'),
		width: '100%',
	},
	describeTextAreaWrapper: {
		flex: '1 1 auto',
		minWidth: '0px',
	},
	modalBodyPadding: {
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.200'),
	},
	remixOptionsStack: {
		width: '100%',
	},
});

// ── RemixOverlayHeader ─────────────────────────────────────────────────────

function RemixOverlayHeader() {
	const { formatMessage } = useIntl();

	return (
		<div css={headerWrapperStyle}>
			<Stack space="space.100" alignInline="center">
				<Tooltip content={formatMessage(messages.labsLozengeTooltip)} position="top">
					{(tooltipProps) => (
						<div css={labsPositionStyle}>
							<Flex {...tooltipProps} justifyContent="end">
								<Lozenge appearance="new">
									<FormattedMessage {...messages.labsLozenge} />
								</Lozenge>
							</Flex>
						</div>
					)}
				</Tooltip>
				<div css={headerIconWrapperStyle} aria-hidden="true">
					<Image
						src={remixIllustrationSvg}
						alt={formatMessage(messages.remixIconAlt)}
						css={headerIconImageStyle}
					/>
				</div>
				<Stack space="space.050" alignInline="center">
					<Heading size="large">
						<FormattedMessage {...messages.heading} />
					</Heading>
					<div css={subtitleStyle}>
						<Text size="small">
							<FormattedMessage {...messages.subtitle} />
						</Text>
					</div>
				</Stack>
			</Stack>
		</div>
	);
}

// ── RemixSubTypeGrid ───────────────────────────────────────────────────────

interface RemixSubTypeGridProps {
	subTypes: RemixSubType[];
	onSubTypeSelect: (subTypeValue: string) => void;
}

function RemixSubTypeGrid({ subTypes, onSubTypeSelect }: RemixSubTypeGridProps) {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={gridStyles.grid}>
			{subTypes.map((subType) => {
				const IconComponent = SUB_TYPE_ICON_MAP[subType.value as keyof typeof SUB_TYPE_ICON_MAP];
				const label = formatMessage(subType.labelMessage);

				if (process.env.NODE_ENV === 'development' && !IconComponent) {
					// eslint-disable-next-line no-console
					console.warn(`[RemixSubTypeGrid] Missing icon for sub-type: "${subType.value}"`);
				}

				return (
					<Tooltip key={subType.value} content={label} position="top">
						{(tooltipProps) => (
							<Pressable
								{...tooltipProps}
								xcss={gridStyles.tile}
								onClick={() => onSubTypeSelect(subType.value)}
								testId={subType.testId}
								aria-label={label}
							>
								{IconComponent ? <IconComponent /> : null}
							</Pressable>
						)}
					</Tooltip>
				);
			})}
		</Box>
	);
}

// ── RemixAccordionOption ───────────────────────────────────────────────────

interface RemixAccordionOptionProps {
	option: RemixOption;
	isExpanded: boolean;
	onToggle: (type: RemixOptionKind) => void;
	onSubTypeSelect: (type: RemixOptionKind, subType: string) => void;
	onPickForMe: (type: RemixOptionKind) => void;
}

function RemixAccordionOption({
	option,
	isExpanded,
	onToggle,
	onSubTypeSelect,
	onPickForMe,
}: RemixAccordionOptionProps) {
	const { formatMessage } = useIntl();

	const handleToggle = useCallback(() => {
		onToggle(option.type);
	}, [onToggle, option.type]);

	const handleSubTypeSelect = useCallback(
		(subTypeValue: string) => {
			onSubTypeSelect(option.type, subTypeValue);
		},
		[onSubTypeSelect, option.type],
	);

	const handlePickForMe = useCallback(() => {
		onPickForMe(option.type);
	}, [onPickForMe, option.type]);

	const ChevronIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;

	return (
		<Stack xcss={accordionStyles.accordionWrapper}>
			<Pressable
				xcss={accordionStyles.accordionHeader}
				onClick={handleToggle}
				testId={option.testId}
				aria-expanded={isExpanded}
				aria-label={formatMessage(option.labelMessage)}
			>
				<Flex alignItems="center" gap="space.100">
					<Box as="span" xcss={accordionStyles.iconContainer}>
						<Image
							src={option.icon}
							srcDark={option.iconDark}
							alt={formatMessage(option.iconAltMessage)}
						/>
					</Box>
					<Text weight="medium">{formatMessage(option.labelMessage)}</Text>
				</Flex>
				<ChevronIcon label="" color={token('color.icon')} />
			</Pressable>

			<div
				css={[revealWrapperBaseStyle, isExpanded ? revealExpandedStyle : revealCollapsedStyle]}
				aria-hidden={!isExpanded}
				{...(!isExpanded ? { inert: '' } : {})}
			>
				<RemixSubTypeGrid
					subTypes={option.subTypes}
					onSubTypeSelect={handleSubTypeSelect}
				/>
				<Box xcss={accordionStyles.pickForMeWrapper}>
					<div css={pickForMeButtonWeightStyle}>
						<Button
							appearance="default"
							onClick={handlePickForMe}
							testId={`${option.testId}-pick-for-me`}
						>
							{formatMessage(messages.pickForMe)}
						</Button>
					</div>
				</Box>
			</div>
		</Stack>
	);
}

// ── RemixOverlay (main export) ─────────────────────────────────────────────

interface RemixOverlayProps {
	onClose: () => void;
	onOptionSelect?: (params: RemixOptionSelectionParameters) => void;
}

/**
 * A centered modal overlay that displays remix options as expandable accordions.
 *
 * Each accordion reveals sub-type tiles the user can pick from.
 * Selecting a sub-type emits `{ type, subType }`.
 * The "Pick for me" button emits only `{ type }`, letting the backend choose.
 * The describe input emits `{ type: 'custom', userPrompt }`.
 */
export function RemixOverlay({ onClose, onOptionSelect }: RemixOverlayProps) {
	const { formatMessage } = useIntl();
	const [describeValue, setDescribeValue] = useState('');
	const [expandedOption, setExpandedOption] = useState<RemixOptionKind | null>(null);

	const handleDescribeSubmit = useCallback(() => {
		if (describeValue?.trim()) {
			onOptionSelect?.({ type: 'custom', userPrompt: describeValue });
		}
	}, [describeValue, onOptionSelect]);

	const handleDescribeKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleDescribeSubmit();
			}
		},
		[handleDescribeSubmit],
	);

	const handleToggle = useCallback((type: RemixOptionKind) => {
		setExpandedOption((prev) => (prev === type ? null : type));
	}, []);

	const handleSubTypeSelect = useCallback(
		(type: RemixOptionKind, subType: string) => {
			onOptionSelect?.({ type, subType });
		},
		[onOptionSelect],
	);

	const handlePickForMe = useCallback(
		(type: RemixOptionKind) => {
			onOptionSelect?.({ type });
		},
		[onOptionSelect],
	);

	return (
		<ModalTransition>
			<Modal onClose={onClose} width={320} testId="remix-overlay" shouldReturnFocus={false}>
				<div css={modalContentWrapperStyle}>
					<ModalHeader hasCloseButton={false}>
						<RemixOverlayHeader />
					</ModalHeader>
					<ModalBody hasInlinePadding={false}>
						<div css={scrollableBodyStyle}>
							<Box xcss={remixStyles.modalBodyPadding}>
								<Stack
									space="space.100"
									alignInline="stretch"
									xcss={remixStyles.remixOptionsStack}
								>
									{REMIX_OPTIONS.map((option) => (
										<RemixAccordionOption
											key={option.type}
											option={option}
											isExpanded={expandedOption === option.type}
											onToggle={handleToggle}
											onSubTypeSelect={handleSubTypeSelect}
											onPickForMe={handlePickForMe}
										/>
									))}
								</Stack>
							</Box>
						</div>
						<Box xcss={remixStyles.describeInputBorderWrapper}>
							<Box xcss={remixStyles.describeInputWrapper}>
								<Form onSubmit={handleDescribeSubmit}>
									<Flex xcss={remixStyles.describePromptRow}>
										<Box xcss={remixStyles.describeTextAreaWrapper}>
											<TextArea
												appearance="none"
												placeholder={formatMessage(messages.describePlaceholder)}
												value={describeValue}
												onChange={(e) => setDescribeValue(e.currentTarget.value)}
												onKeyDown={handleDescribeKeyDown}
												isCompact
												minimumRows={1}
												maxHeight="200px"
												resize="smart"
												aria-label={formatMessage(messages.describePlaceholder)}
												testId="remix-overlay-describe-input"
												name="remix-overlay-describe"
											/>
										</Box>
										<IconButton
											type="submit"
											appearance="primary"
											isDisabled={!describeValue?.trim()}
											icon={ArrowUpIcon}
											label={formatMessage(messages.describeSubmitLabel)}
											testId="remix-overlay-describe-submit"
										/>
									</Flex>
								</Form>
							</Box>
						</Box>
					</ModalBody>
				</div>
			</Modal>
		</ModalTransition>
	);
}
